import { useState } from 'react';
import { api } from '@/api/client';
import { RewriteResumeResponse } from '@/api/types';
import { handleAppError } from '@/lib/errorUtils';
import { AIGenerationHookParams } from './ai/types';

export function useTailoredResume({
  workspaceId,
  selectedId,
  jobText,
  targetDetails,
  setStatus,
  setStatusMessage,
  recordUsage,
  setRightView,
  getSignal,
  setIsGenerating,
  scrollToResults,
}: AIGenerationHookParams) {
  const [tailoredResumeResult, setTailoredResumeResult] = useState<RewriteResumeResponse | null>(null);

  const handleGenerateTailoredResume = async (providedRecommendations?: any[], skipStateUpdates: boolean | any = false) => {
    const isSkip = skipStateUpdates === true;
    if (!selectedId || jobText.trim().length < 50) {
      if (!isSkip) {
        setStatus('error');
        setStatusMessage('Please enter a longer, meaningful job description.');
      }
      return;
    }
    
    setIsGenerating(prev => ({ ...prev, tailoredResume: true }));
    if (!isSkip) {
      setStatus('idle');
      setRightView('tailoredResume');
      scrollToResults();
    }

    try {
      if (!isSkip) await new Promise(r => setTimeout(r, 300));
      const recommendations = Array.isArray(providedRecommendations) ? providedRecommendations : [];
      const customInstructions = `Update the resume to match the company: ${targetDetails?.company || 'Unknown'} and position: ${targetDetails?.position || 'Unknown'}. Please make the resume ATS-friendly for this role.`;

      const signal = isSkip ? getSignal('package') : getSignal('tailoredResume');
      const result = await api.rewriteResume(workspaceId, { 
        resumeId: selectedId, 
        jobText, 
        recommendations,
        customInstructions
      }, { signal });
      if (result.usage) {
        recordUsage(result.usage.cost, result.usage.promptTokens, result.usage.completionTokens);
      }
      
      setTailoredResumeResult(result);
      if (!isSkip) setStatusMessage('Resume tailored');
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      if (!isSkip) {
        setStatus('error');
        setStatusMessage(handleAppError(err, 'Generation failed'));
      }
      throw err;
    } finally {
      setIsGenerating(prev => ({ ...prev, tailoredResume: false }));
    }
  };

  return { tailoredResumeResult, setTailoredResumeResult, handleGenerateTailoredResume };
}
